import { useEffect } from 'react';
import SiteHeader from '../komponenter/SiteHeader';
import InquiryForm from '../komponenter/InquiryForm';
import PropertyHeroSection from '../komponenter/property/PropertyHeroSection';
import PropertyLoadingState from '../komponenter/property/PropertyLoadingState';
import PropertyMapSection from '../komponenter/property/PropertyMapSection';
import PropertyNotFoundState from '../komponenter/property/PropertyNotFoundState';
import { usePropertyDetail } from '../hooks/usePropertyDetail';
import { useSeo } from '../hooks/useSeo';

function PropertyDetailPage() {
  const { error, isLoading, property, returnTo } = usePropertyDetail();

  useSeo({
    canonicalPath: property ? `/eiendom/${property.slug}` : '/eiendom',
    description: property
      ? `${property.name} i ${property.address}. ${property.summary}`
      : 'Detaljside for eiendom hos OmsorgBygg.no.',
    jsonLd: property
      ? {
          '@context': 'https://schema.org',
          '@type': 'Residence',
          name: property.name,
          description: property.summary,
          address: {
            '@type': 'PostalAddress',
            streetAddress: property.address,
            postalCode: property.areaCode || undefined,
            addressCountry: 'NO',
          },
        }
      : null,
    title: property ? `${property.name} | OmsorgBygg.no` : 'Eiendom | OmsorgBygg.no',
  });

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll('[data-detail-reveal]'));

    if (revealElements.length === 0) {
      return undefined;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => {
        element.classList.add('is-visible');
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );

    revealElements.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isLoading, property?.slug]);

  if (isLoading) {
    return (
      <>
        <SiteHeader detailLink={returnTo} />
        <PropertyLoadingState returnTo={returnTo} />
      </>
    );
  }

  if (!property) {
    return (
      <>
        <SiteHeader detailLink={returnTo} />
        <PropertyNotFoundState error={error} returnTo={returnTo} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-cream-200 text-forest-800">
      <SiteHeader detailLink={returnTo} />
      <main className="site-rail py-12 lg:py-16" id="hovedinnhold" tabIndex="-1">
        <div data-detail-reveal>
          <PropertyHeroSection property={property} />
        </div>
        <div data-detail-reveal>
          <PropertyMapSection property={property} />
        </div>
        <section
          className="mt-8 overflow-hidden rounded-[2rem] border border-white/10 bg-forest-950 p-8 shadow-lift md:p-12"
          data-detail-reveal
          id="meld-interesse"
        >
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sand-300">
                <span className="h-px w-8 bg-sand-300" />
                Meld interesse
              </p>
              <h2 className="mt-5 font-serif text-[clamp(1.9rem,3.4vw,2.8rem)] font-bold leading-[1.08] text-cream-50">
                Interessert i denne eiendommen?
              </h2>
              <p className="mt-5 max-w-[52ch] text-[1.06rem] leading-[1.75] text-cream-200/85">
                Send oss en henvendelse, så tar vi kontakt. Telefon og e-post legges til snart.
              </p>
            </div>
            <div className="rounded-[1.75rem] bg-cream-100 p-7 shadow-lift md:p-8">
              <InquiryForm propertyName={property.name} propertySlug={property.slug} />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default PropertyDetailPage;